import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { MovieRow } from "@/components/MovieRow";
import { MovieCard } from "@/components/MovieCard";
import { AdSlot } from "@/components/AdSlot";
import { Footer } from "@/components/Footer";
import { MagazineBlock, MagazineRow } from "@/components/Magazine";
import { FestivalsSection } from "@/components/FestivalsSection";
import { useLang } from "@/context/LanguageContext";
import { SITE_URL } from "@/lib/api";
import { Film, Tv, Sparkles, Heart, Users, User, Tv2, Smile, Clapperboard, BookOpen } from "lucide-react";

export default function Browse() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [mediaFilter, setMediaFilter] = useState("all"); // 'all', 'movie', 'tv'

  const loadHome = useCallback(async () => {
    const { data } = await api.get("/home", { params: { lang } });
    setData(data);
  }, [lang]);

  useEffect(() => { loadHome(); }, [loadHome]);

  useEffect(() => {
    if (!search.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      const { data } = await api.get(`/movies`, { params: { search } });
      setResults(Array.isArray(data) ? data : (data?.movies || []));
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Map category icons
  const getCategoryIcon = (id) => {
    switch (id) {
      case "cat-gay": return <Users size={16} className="text-[#E11D48]" />;
      case "cat-lesbico": return <Users size={16} className="text-purple-400" />;
      case "cat-trans": return <User size={16} className="text-cyan-400" />;
      case "cat-series": return <Tv2 size={16} className="text-blue-400" />;
      case "cat-romance": return <Heart size={16} className="text-pink-400" />;
      case "cat-comedia": return <Smile size={16} className="text-amber-400" />;
      case "cat-documental": return <Clapperboard size={16} className="text-emerald-400" />;
      case "cat-cortos": return <Film size={16} className="text-amber-300" />;
      default: return <Film size={16} className="text-zinc-400" />;
    }
  };

  // Compute active row for filtered category view
  const activeRow = useMemo(() => {
    if (!data || !data.rows || selectedCategory === "all") return null;
    return data.rows.find((r) => r.category.id === selectedCategory || r.category.slug === selectedCategory);
  }, [data, selectedCategory]);

  // Filter movies in active category by media type
  const filteredCategoryMovies = useMemo(() => {
    if (!activeRow) return [];
    if (mediaFilter === "all") return activeRow.movies;
    return activeRow.movies.filter((m) => m.media_type === mediaFilter);
  }, [activeRow, mediaFilter]);

  return (
    <div className="min-h-screen bg-[#050505] pb-16">
      <Helmet>
        <title>La Caja LGBT — Cine, series y documentales LGBTQ+</title>
        <meta name="description" content="El mayor catálogo de cine y series LGBTQ+ en español, inglés y francés. Descubre películas, series y documentales gay, lésbico, bisexual y trans." />
        <link rel="canonical" href={`${SITE_URL}/`} />
        <link rel="alternate" hrefLang="es" href={`${SITE_URL}/?lang=es`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/?lang=en`} />
        <link rel="alternate" hrefLang="fr" href={`${SITE_URL}/?lang=fr`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/`} />
        <meta property="og:title" content="La Caja LGBT — Cine y series LGBTQ+" />
        <meta property="og:url" content={`${SITE_URL}/`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
      </Helmet>
      <Navbar onSearch={setSearch} search={search} />

      {results ? (
        <div className="pt-24 px-6 md:px-16" data-testid="search-results">
          <h2 className="font-heading text-2xl mb-6">{t("resultsFor")} "{search}"</h2>
          {results.length === 0 ? (
            <p className="text-zinc-400">{t("notFound")}</p>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                {results.map((m) => <MovieCard key={m.id} movie={m} className="w-full" />)}
              </div>
              <div className="my-10"><AdSlot slot="7187293245" /></div>
            </>
          )}
        </div>
      ) : !data ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <Hero movie={data.hero} />

          {/* Magazine Editorial Masthead Strip */}
          <div className="bg-[#0A0A0C] border-y border-white/10 py-3.5 px-6 md:px-16 flex flex-wrap items-center justify-between gap-3 text-xs tracking-wider uppercase font-semibold text-zinc-400 relative z-20">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-[#E11D48] animate-pulse" />
              <span className="text-white font-black tracking-widest text-sm">LA CAJA LGBT</span>
              <span className="hidden sm:inline text-zinc-600">·</span>
              <span className="hidden sm:inline text-zinc-300 font-medium">REVISTA DIGITAL DE CINE, SERIES & CULTURA QUEER</span>
            </div>
            <div className="flex items-center gap-4 text-[11px] text-zinc-400">
              <Link to="/revista" className="hover:text-[#E11D48] transition-colors flex items-center gap-1.5 font-bold tracking-widest text-[#E11D48]">
                <BookOpen size={14} /> EDICIÓN DIGITAL & CRÍTICAS →
              </Link>
            </div>
          </div>

          <div className="relative z-10 mt-8 md:mt-12">
            {/* Category Filter Navigation Bar */}
            <div className="px-6 md:px-16 mb-10 md:mb-14 overflow-x-auto no-scrollbar py-2">
              <div className="flex items-center gap-2 max-w-full">
                <button
                  onClick={() => { setSelectedCategory("all"); setMediaFilter("all"); }}
                  className={`px-4 py-2 text-xs md:text-sm rounded-full font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                    selectedCategory === "all"
                      ? "bg-[#E11D48] text-white shadow-lg shadow-[#E11D48]/20"
                      : "bg-zinc-900/80 text-zinc-300 hover:bg-zinc-800 border border-white/5"
                  }`}
                >
                  <Sparkles size={15} /> Todo el catálogo
                </button>

                {(data.rows || []).map((row) => {
                  const isActive = selectedCategory === row.category.id || selectedCategory === row.category.slug;
                  return (
                    <button
                      key={row.category.id}
                      onClick={() => { setSelectedCategory(row.category.id); setMediaFilter("all"); }}
                      className={`px-4 py-2 text-xs md:text-sm rounded-full font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                        isActive
                          ? "bg-white text-black shadow-lg"
                          : "bg-zinc-900/80 text-zinc-300 hover:bg-zinc-800 border border-white/5"
                      }`}
                    >
                      {getCategoryIcon(row.category.id)}
                      {row.category.name}
                      <span className="text-[10px] opacity-60 bg-black/20 px-1.5 py-0.5 rounded-full">
                        {row.movies.length}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Filtered Category Grid View */}
            {selectedCategory !== "all" && activeRow ? (
              <div className="px-6 md:px-16 mb-12 animate-fade-in">
                <div className="bg-gradient-to-r from-zinc-900/90 to-zinc-950 p-6 md:p-8 rounded-xl border border-white/10 mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#E11D48] mb-1 font-semibold">
                      {getCategoryIcon(activeRow.category.id)} Categoría Destacada
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                      {activeRow.category.name}
                    </h2>
                    {activeRow.category.description && (
                      <p className="text-zinc-400 text-sm max-w-2xl">{activeRow.category.description}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 bg-black/40 p-1 rounded-lg border border-white/5 self-start md:self-auto">
                    <button
                      onClick={() => setMediaFilter("all")}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors ${mediaFilter === "all" ? "bg-[#E11D48] text-white" : "text-zinc-400 hover:text-white"}`}
                    >
                      Todos ({activeRow.movies.length})
                    </button>
                    <button
                      onClick={() => setMediaFilter("movie")}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1 ${mediaFilter === "movie" ? "bg-[#E11D48] text-white" : "text-zinc-400 hover:text-white"}`}
                    >
                      <Film size={12} /> Películas
                    </button>
                    <button
                      onClick={() => setMediaFilter("tv")}
                      className={`px-3 py-1.5 text-xs rounded-md transition-colors flex items-center gap-1 ${mediaFilter === "tv" ? "bg-[#E11D48] text-white" : "text-zinc-400 hover:text-white"}`}
                    >
                      <Tv size={12} /> Series
                    </button>
                  </div>
                </div>

                {filteredCategoryMovies.length === 0 ? (
                  <div className="py-12 text-center text-zinc-500">
                    No hay títulos que coincidan con este filtro en {activeRow.category.name}.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6">
                    {filteredCategoryMovies.map((m) => (
                      <MovieCard key={m.id} movie={m} className="w-full" />
                    ))}
                  </div>
                )}
                
                <div className="my-10"><AdSlot slot="4752701591" /></div>
              </div>
            ) : (
              /* All Catalog Organized View */
              <>
                {data.articles?.length > 0 && <MagazineBlock articles={data.articles} />}

                {data.continue_watching?.length > 0 && (
                  <MovieRow title={t("continueWatching")} movies={data.continue_watching} testid="row-continue-watching" />
                )}

                {data.my_list?.length > 0 && (
                  <MovieRow title={t("myList")} movies={data.my_list} testid="row-my-list" />
                )}

                {(data.rows || []).slice(0, 3).map((row) => (
                  <MovieRow key={row.category.id} title={row.category.name} movies={row.movies} testid={`row-${row.category.slug}`} />
                ))}

                <div className="px-6 md:px-16 my-12 md:my-16"><AdSlot slot="4752701591" /></div>

                {data.articles?.length >= 4 && <MagazineRow articles={data.articles} />}

                {(data.rows || []).slice(3).map((row) => (
                  <MovieRow key={row.category.id} title={row.category.name} movies={row.movies} testid={`row-${row.category.slug}`} />
                ))}

                <FestivalsSection />

                <div className="px-6 md:px-16 my-12 md:my-16"><AdSlot slot="8500374913" /></div>
              </>
            )}
          </div>

          <Footer />
        </>
      )}
    </div>
  );
}

